import path from "node:path";
import { downloadImage, getFilenameFromUrl, type Locator, type Response, success } from "@iflyrpa/share";
import type { PublishAction } from "./index";
export const rpaAction: PublishAction = async (task, params) => {
    // const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");
    const commonCookies = {
        path: "/",
        secure: true,
        domain: "douyin.com",
        url: "https://creator.douyin.com",
        httpOnly: true,
    };
    const page = await task.createPage({
        show: task.debug,
        url: params.url || "https://creator.douyin.com/creator-micro/content/upload?default-tab=3",
        // url:'https://www.browserscan.net/zh/bot-detection',
        cookies: params.cookies?.map((it) => ({ ...it, ...commonCookies })),
    });
    // await updateTaskState?.({
    //     state: TaskState.ACTION,
    //     connectAddress: task.steelConnector?.getProxyUrl(task.sessionId || "", "v1/sessions/debug"),
    //     sessionId: task.sessionId,
    // });

    const tmpCachePath = task.getTmpPath();

    // Helper functions for robust interactions
    const waitForElement = async (selector: string, timeout = 10000): Promise<Locator | null> => {
        try {
            const element = page.locator(selector);
            task.logger.warn(`element: ${element}`);
            await element.waitFor({ state: "visible", timeout });
            return element;
        } catch {
            task.logger.warn(`元素未找到: ${selector}`);
            return null;
        }
    };

    const retryAction = async <T>(action: () => Promise<T>, maxRetries = 3, delay = 1000): Promise<T> => {
        let lastError: Error | undefined;
        for (let i = 0; i < maxRetries; i++) {
            try {
                return await action();
            } catch (error) {
                lastError = error as Error;
                task.logger.warn(`重试 ${i + 1}/${maxRetries}: ${lastError.message}`);
                if (i < maxRetries - 1) {
                    await page.waitForTimeout(delay);
                }
            }
        }
        throw lastError;
    };

    const safeClick = async (locator: Locator | null): Promise<boolean> => {
        if (!locator) return false;
        try {
            await locator.click({ timeout: 5000 });
            return true;
        } catch (error) {
            task.logger.warn(`点击失败: ${error}`);
            return false;
        }
    };

    const safeFill = async (locator: Locator | null, text: string): Promise<boolean> => {
        if (!locator || !text) return false;
        try {
            await locator.clear({ timeout: 3000 });
            await locator.fill(text, { timeout: 5000 });
            return true;
        } catch (error) {
            task.logger.warn(`填充文本失败: ${error}`);
            return false;
        }
    };

    try {
        task.logger.info("检查登录状态...");
        await page.waitForSelector("#micro", {
            state: "visible",
            timeout: 20000,
        });
        task.logger.info("✓ 登录状态正常");
    } catch (_error) {
        task.logger.error("✗ 登录失效或页面加载异常");
        return { code: 414, message: "登录失效", data: page.url() };
    }
    // 上传图片
    if (params.banners && params.banners.length > 0 && params.coverImage && params.coverImage.length > 0) {
        const imgs = [params.coverImage].concat(params.banners);
        task.logger.info(`开始上传 ${imgs.length} 张图片`);

        await retryAction(async () => {
            // 下载图片到本地
            const images = await Promise.all(
                imgs.map((url) => {
                    const fileName = getFilenameFromUrl(url);
                    return downloadImage(url, path.join(tmpCachePath, fileName));
                }),
            );

            // 等待文件上传输入框
            const fileChooser = await page.waitForSelector('input[type="file"]', { timeout: 20000 });
            if (!fileChooser) {
                throw new Error("未找到图片上传输入框");
            }

            await fileChooser.setInputFiles(images);
            task.logger.info(`图片上传成功: ${images.length} 张`);

            // 等待图片处理
            await page.waitForTimeout(2000);
        });
    }
    // 填写标题
    if (params.title) {
        task.logger.info(`填写标题: ${params.title}`);
        await retryAction(async () => {
            const titleInstance = await waitForElement(".semi-input-wrapper input[placeholder='添加作品标题']", 50000);
            if (!titleInstance) {
                throw new Error("未找到标题输入框");
            }
            await titleInstance.click();
            await safeFill(titleInstance, params.title);
            task.logger.info("标题填写完成");
        });
    }
    // 填写描述
    if (params.content) {
        task.logger.info("填写描述");
        await page.waitForTimeout(1000);
        await retryAction(async () => {
            const descInstance = await waitForElement("div[data-placeholder='添加作品描述...']", 10000);
            if (!descInstance) {
                throw new Error("未找到描述编辑器");
            }
            await descInstance.click();
            await page.waitForTimeout(500);

            // 清理内容中的话题标记
            const cleanContent = params.content.replace(/#.*?\[.*?]#/g, "");
            await descInstance.pressSequentially(cleanContent, { delay: 10 });
            task.logger.info("描述填写完成");
        });
    }

    // 添加话题标签
    if (params.topic && params.topic.length > 0) {
        task.logger.info(`添加话题标签: ${params.topic.length} 个`);
        try {
            const topics = params.topic; // 保存到局部变量避免 TypeScript 类型问题
            await retryAction(async () => {
                const descInstance = page.locator(".editor-kit-container.editor.editor-comp-publish");

                // 遍历话题数组
                for (const topic of topics) {
                    if (topic.cha_name) {
                        // 输入话题标签格式: #话题名 (带空格)
                        const topicTag = `#${topic.cha_name} `;
                        await descInstance.pressSequentially(topicTag, { delay: 10 });
                        task.logger.info(`已添加话题: ${topicTag.trim()}`);
                        await page.waitForTimeout(300);
                    }
                }

                task.logger.info("✓ 话题标签添加完成");
            });
        } catch (error) {
            task.logger.warn(`添加话题标签失败: ${error}`);
        }
    }

    // 选择合集
    if (params.mix?.mix_name) {
        task.logger.info(`选择合集: ${params.mix.mix_name}`);
        const instanceCollection = page.locator("[class^='mix-sel-wrap'] .semi-select").nth(1);
        await instanceCollection.click();
        await page.waitForTimeout(2000);
        page.locator(".semi-select-option-list .semi-select-option")
            .filter({ hasText: params.mix.mix_name })
            .first()
            .click({ force: true });
    }

    // 选择合集
    if (params.mix?.mix_order_name) {
        task.logger.info(`选择合集集数: ${params.mix.mix_order_name}`);
        const instanceCollection = page.locator("[class^='mix-sel-wrap'] .semi-select").nth(2);
        await instanceCollection.click();
        await page.waitForTimeout(2000);
        page.locator(".mix-order-dropdown .semi-select-option")
            .filter({ hasText: params.mix.mix_order_name })
            .first()
            .click({ force: true });
    }

    // 选择音乐
    if (params.musicId && params.musicName) {
        task.logger.info(`选择音乐: ${params.musicName} - ${params.musicAuthor || ""}`);
        try {
            await retryAction(async () => {
                // 点击音乐选择触发器
                const musicTrigger = await waitForElement("[class^='container-right-']", 10000);
                if (!musicTrigger) {
                    throw new Error("未找到音乐选择触发器");
                }
                await musicTrigger.click();
                task.logger.info("已打开音乐选择面板");
                await page.waitForTimeout(1000);
                const searchBox = await waitForElement("[class^='music-search']", 10000);
                if (!searchBox) {
                    throw new Error("未找到音乐搜索框");
                }
                await searchBox.click();
                await page.waitForTimeout(500);

                // 填充搜索内容
                const searchInput = page.locator("[class^='music-search'] input");
                const searchText = params.musicAuthor
                    ? `${params.musicName} - ${params.musicAuthor}`
                    : params.musicName;
                await searchInput.fill(searchText || "");
                task.logger.info(`已输入搜索内容: ${searchText}`);

                // 等待输入稳定
                await page.waitForTimeout(300);

                // 按回车触发搜索
                await searchInput.press("Enter");
                task.logger.info("已触发搜索");

                // 等待搜索结果加载
                await page.waitForTimeout(4000);

                // 等待搜索结果容器出现
                const resultContainer = page.locator("[class^='card-container-']");
                task.logger.info(`resultContainer: ${resultContainer}`);

                if (!resultContainer) {
                    throw new Error("未找到音乐搜索结果");
                }

                // 查找匹配的音乐项
                const musicCards = page.locator("[class^='card-container-']");
                const matchedCard = musicCards.filter({ hasText: params.musicName }).first();

                const cardCount = await matchedCard.count();
                if (cardCount === 0) {
                    task.logger.warn(`未找到匹配的音乐: ${params.musicName}`);
                    throw new Error(`未找到匹配的音乐: ${params.musicName}`);
                }

                task.logger.info("找到匹配的音乐，准备选择");

                // hover 到音乐卡片上显示按钮
                await matchedCard.hover();
                task.logger.info("已 hover 到音乐卡片");
                await page.waitForTimeout(2000);

                // 点击选择按钮
                const selectButton = matchedCard.locator(".semi-button").first();
                const buttonClicked = await safeClick(selectButton);

                if (!buttonClicked) {
                    throw new Error("点击音乐选择按钮失败");
                }

                task.logger.info("✓ 音乐选择完成");
                await page.waitForTimeout(500);
            });
        } catch (error) {
            task.logger.warn(`选择音乐失败: ${error}`);
        }
    }

    // 选择热点
    if (params.hotSentence) {
        task.logger.info(`选择热点: ${params.hotSentence}`);
        const instance = page.locator(".semi-select-filterable").nth(1);
        await instance.click();
        await page.waitForTimeout(1000);
        await page.locator(".semi-input-wrapper input").nth(1).fill(params.hotSentence);
        await page.waitForTimeout(2000);
        page.locator(".semi-popover-content .semi-select-option")
            .filter({ hasText: params.hotSentence })
            .first()
            .click({ force: true });
    }
    // if (params.topic && params.topic.length > 0) {
    //     for (const it of params.topic) {
    //         await descInstance.pressSequentially(`#${it.word}`);
    //         await page.locator(".ql-mention-loading, .topic-loading").waitFor({ state: "detached" });
    //         await page.waitForTimeout(1000);
    //         await page.locator(".topic-item.is-selected, .creator-editor-topic-container .item.is-selected").click();
    //     }
    // }

    // const container = page.locator(".plugin.editor-container, .content-container");
    // await container.focus();
    // await page.mouse.wheel(0, 500);

    // if (params.address) {
    //     await selectAddress(
    //         page.locator(".media-extension .address-input, .location-input").filter({ hasText: "添加地点" }),
    //         params.address.name,
    //     );
    // }
    // 设置可见范围
    if (params.visibleRange) {
        task.logger.info(`设置可见范围: ${params.visibleRange}`);
        if (params.visibleRange === "2") {
            // 2 = 好友可见
            await page.locator("label:has-text('好友可见')").first().click();
            task.logger.info("已设置为好友可见");
        } else if (params.visibleRange === "1") {
            // 1 = 仅自己可见
            await page.locator("label:has-text('仅自己可见')").first().click();
            task.logger.info("已设置为仅自己可见");
        }
        // 0 = 公开（默认，无需操作）
    }

    // 设置保存权限
    if (!params.allowSave) {
        task.logger.info(`设置保存权限: ${params.allowSave ? "允许" : "不允许"}`);
        try {
            // false = 不允许
            await page.locator("label:has-text('不允许')").first().click();
            task.logger.info("已设置为保存权限不允许");
            await page.waitForTimeout(300);
        } catch (error) {
            task.logger.warn(`设置保存权限失败: ${error}`);
        }
    }

    // 设置定时发布
    if (!params.isImmediatelyPublish && params.scheduledPublish) {
        task.logger.info("设置定时发布");

        try {
            // 点击定时发布单选按钮
            await page.locator('label:has-text("定时发布")').first().click();
            task.logger.info("已选择定时发布");
            await page.waitForTimeout(500);

            // 归一化时间戳（处理秒/毫秒）
            const timestamp =
                params.scheduledPublish > 10000000000 ? params.scheduledPublish / 1000 : params.scheduledPublish;
            const publishDate = new Date(timestamp * 1000);

            // 格式化时间字符串
            const year = publishDate.getFullYear();
            const month = String(publishDate.getMonth() + 1).padStart(2, "0");
            const day = String(publishDate.getDate()).padStart(2, "0");
            const hour = String(publishDate.getHours()).padStart(2, "0");
            const minute = String(publishDate.getMinutes()).padStart(2, "0");
            const formattedTime = `${year}-${month}-${day} ${hour}:${minute}`;

            task.logger.info(`目标发布时间: ${formattedTime}`);

            // 填写时间
            const releaseTimeInstance = await waitForElement(
                ".semi-datepicker-input input[placeholder='日期和时间']",
                10000,
            );
            if (!releaseTimeInstance) {
                throw new Error("未找到时间输入框");
            }

            await releaseTimeInstance.click();
            await page.waitForTimeout(500);
            await releaseTimeInstance.fill(formattedTime);
            await page.waitForTimeout(500);

            // 验证输入值
            const inputValue = await releaseTimeInstance.inputValue();
            if (inputValue !== formattedTime) {
                task.logger.warn(`时间输入验证失败，期望: ${formattedTime}, 实际: ${inputValue}`);
            } else {
                task.logger.info("定时发布设置完成");
            }

            await releaseTimeInstance.blur();
            await page.waitForTimeout(500);
        } catch (error) {
            task.logger.warn(`定时发布设置失败: ${error}`);
        }
    }

    // 点击发布按钮
    task.logger.info("准备发布...");
    await page.waitForTimeout(1000);

    const response = await new Promise<string>((resolve) => {
        const handleResponse = async (response: Response) => {
            if (response.url().includes("/web/api/media/aweme/create_v2")) {
                console.log("匹配到发布接口响应");
                const jsonResponse = await response.json();
                console.log("发布接口响应数据:", jsonResponse);
                page.off("response", handleResponse);
                resolve(jsonResponse?.data?.item_id);
            }
        };
        console.log("监听响应事件");
        page.on("response", handleResponse);
        console.log("点击发布按钮");
        // 在设置监听器后等待，然后点击
        setTimeout(() => {
            page.locator("#DCPF button:has-text('发布')").first().click();
            console.log("发布按钮已点击");
        }, 1000);
    });

    // await updateTaskState?.({
    //     state: TaskState.SUCCESS,
    //     result: {
    //         response,
    //     },
    // });
    console.log("发布成功了");

    await page.close();

    return success(response);
};
